// LUCKY DUCKY · PHASE 2 · Developer simulator
// Uses the SAME evaluator as the playable game (reelEngine.js) so telemetry
// reflects real production behavior — not a shadow model.
//
// Report shape mirrors Phase 2 rule #11 exactly:
//   total spins, RTP, any-win freq, line-win freq, 2+ BONUS freq, 3+ BONUS freq,
//   feature-entry freq, retrigger freq, DOUBLE/WILD contribution,
//   base-game RTP contribution, feature RTP contribution, avg feature value,
//   win distribution, p99 win, p99.9 win, max observed, unresolved-edge counts.
import { spinGrid, evaluateGrid } from './reelEngine';
import { WAGER_CREDITS } from './wager';
import { RULES } from './paytable';
import { defaultRespin, FEATURE_FLAGS } from './bonusState';

function pctile(sorted, p) {
  if (!sorted.length) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
  return sorted[idx];
}

/**
 * @param {number} spins  How many base-game spins to simulate.
 * @param {string} wagerKey  Which wager to bet each spin (PLAY_1X, PLAY_2X, ...).
 */
export function simulate({ spins = 200000, wagerKey = 'PLAY_1X' } = {}) {
  const wagerCredits = WAGER_CREDITS[wagerKey];
  if (!wagerCredits) throw new Error(`Unknown wager key ${wagerKey}`);

  let anyWinCount = 0;
  let lineWinCount = 0;
  let bonus2plusCount = 0;
  let bonus3plusCount = 0;
  let featureEntries = 0;
  let featureFreeplaysExecuted = 0;
  let retriggerCount = 0;
  let mixed3plusWildFlagged = 0;

  let totalReturn = 0;
  let baseReturn = 0;
  let featureReturn = 0;
  let wildContribution = 0;
  const featureValues = [];
  const winValues = [];

  for (let i = 0; i < spins; i++) {
    // Base spin
    const grid = spinGrid();
    const ev = evaluateGrid(grid, false);
    let spinReturn = ev.totalWin;
    baseReturn += ev.totalWin;
    if (ev.flags.mixed_3plus_wild_flagged) mixed3plusWildFlagged += ev.flags.mixed_3plus_wild_flagged;
    ev.lineWins.forEach(w => {
      if (w.kind === 'wild-assisted' || w.kind === 'wild-standalone') wildContribution += w.pay;
    });
    if (ev.lineWins.length > 0) lineWinCount++;
    if (ev.totalWin > 0) anyWinCount++;
    if (ev.bonusCount >= 2) bonus2plusCount++;
    if (ev.bonusCount >= 3) bonus3plusCount++;

    // Respin path — 2 BONUS exactly: DEFAULT single respin, no holds (NEEDS_VERIFICATION)
    if (ev.bonusCount === 2 && !FEATURE_FLAGS.respin_hold_and_lock) {
      const rp = defaultRespin(spinGrid, evaluateGrid);
      spinReturn += rp.evaluation.totalWin;
      baseReturn += rp.evaluation.totalWin;
      if (rp.evaluation.bonusCount >= 3) {
        // Respin escalated to feature entry.
        ev.enters_free_plays = true;
      }
    }

    // Feature entry
    if (ev.enters_free_plays || ev.bonusCount >= 3) {
      featureEntries++;
      let featWin = 0;
      let plays = RULES.free_plays_awarded_on_trigger;
      while (plays > 0) {
        featureFreeplaysExecuted++;
        const fg = spinGrid();
        const fe = evaluateGrid(fg, true);
        featWin += fe.totalWin;
        featureReturn += fe.totalWin;
        // Retrigger per PROD-10 / PROD-11 (PRODUCTION_SPEC): +10 free plays
        // when 3+ BONUS on a free-play grid; retriggered plays can themselves
        // retrigger while retrigger_can_stack is true.
        if (FEATURE_FLAGS.retrigger_plus10_free_plays && fe.enters_free_plays) {
          retriggerCount++;
          if (FEATURE_FLAGS.retrigger_can_stack) plays += 10;
          else if (retriggerCount === 1) plays += 10;   // non-stacking: only first retrigger adds
        }
        plays--;
      }
      spinReturn += featWin;
      featureValues.push(featWin);
    }

    totalReturn += spinReturn;
    if (spinReturn > 0) winValues.push(spinReturn);
  }

  // Wins scale linearly with the bet (server model: apply_wager_scale), so
  // returns evaluated in base-wager credits are scaled up to the actual
  // wager here.  This keeps RTP identical across every wager level.
  const scale = wagerCredits / WAGER_CREDITS.PLAY_1X;
  if (scale !== 1) {
    totalReturn = Math.round(totalReturn * scale);
    baseReturn = Math.round(baseReturn * scale);
    featureReturn = Math.round(featureReturn * scale);
    wildContribution = Math.round(wildContribution * scale);
    for (let i = 0; i < winValues.length; i++) winValues[i] = Math.round(winValues[i] * scale);
    for (let i = 0; i < featureValues.length; i++) featureValues[i] = Math.round(featureValues[i] * scale);
  }

  const totalWagered = spins * wagerCredits;
  const rtp = (totalReturn / totalWagered) * 100;

  winValues.sort((a, b) => a - b);
  featureValues.sort((a, b) => a - b);

  const featureAvg = featureValues.length
    ? featureValues.reduce((s, v) => s + v, 0) / featureValues.length : 0;
  const featureMed = featureValues.length ? featureValues[Math.floor(featureValues.length / 2)] : 0;

  const winDist = { '0-10': 0, '11-50': 0, '51-200': 0, '201-1000': 0, '1000+': 0 };
  winValues.forEach(v => {
    if (v <= 10)     winDist['0-10']++;
    else if (v <= 50)   winDist['11-50']++;
    else if (v <= 200)  winDist['51-200']++;
    else if (v <= 1000) winDist['201-1000']++;
    else                winDist['1000+']++;
  });

  return {
    spec_version: 'Cardinal V2 · provisional strips',
    wager: { key: wagerKey, credits: wagerCredits },
    spins,
    rtp_percent: Number(rtp.toFixed(4)),
    total_wagered_credits: totalWagered,
    total_returned_credits: totalReturn,
    any_win_frequency_pct:    Number(((anyWinCount / spins) * 100).toFixed(4)),
    line_win_frequency_pct:   Number(((lineWinCount / spins) * 100).toFixed(4)),
    bonus2plus_frequency_pct: Number(((bonus2plusCount / spins) * 100).toFixed(4)),
    bonus3plus_frequency_pct: Number(((bonus3plusCount / spins) * 100).toFixed(4)),
    feature_entry_frequency_pct: Number(((featureEntries / spins) * 100).toFixed(4)),
    feature_entry_approx_1_in: featureEntries > 0 ? Math.round(spins / featureEntries) : null,
    retrigger_frequency_pct: Number(((retriggerCount / Math.max(1, featureEntries)) * 100).toFixed(4)),
    wild_contribution_percent_of_return: Number(((wildContribution / Math.max(1, totalReturn)) * 100).toFixed(4)),
    base_game_rtp_contribution_pct:   Number(((baseReturn    / totalWagered) * 100).toFixed(4)),
    feature_rtp_contribution_pct:     Number(((featureReturn / totalWagered) * 100).toFixed(4)),
    avg_feature_value_credits:    Number(featureAvg.toFixed(2)),
    median_feature_value_credits: featureMed,
    win_distribution_buckets: winDist,
    p99_win_credits:   pctile(winValues, 0.99),
    p999_win_credits:  pctile(winValues, 0.999),
    max_observed_win_credits: winValues[winValues.length - 1] || 0,
    unresolved_edge_cases: {
      mixed_3plus_wild_standard_flagged: mixed3plusWildFlagged,
      note: 'Mixed 3+ Wild + standard-symbol lines paid 0 credits and are counted here. NEEDS_VERIFICATION.',
    },
    feature_flags: FEATURE_FLAGS,
  };
}
